import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ChevronLeft, UserPlus, UserCheck, Play, Image as ImageIcon } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useToast } from "@/components/Toast";

function compactNum(n) {
  const v = Number(n) || 0;
  if (v < 1000) return String(v);
  if (v < 1_000_000) return `${(v / 1000).toFixed(v < 10_000 ? 1 : 0)}K`;
  return `${(v / 1_000_000).toFixed(1)}M`;
}

export default function CreatorProfileScreen() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [following, setFollowing] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data: d } = await api.get(`/feed/users/${userId}`);
        setData(d);
        setFollowing(!!d.is_following);
      } catch (err) {
        setError(formatApiError(err));
      }
    })();
  }, [userId]);

  const toggleFollow = async () => {
    if (busy || data?.is_self) return;
    setBusy(true);
    const optimistic = !following;
    setFollowing(optimistic);
    try { await api.post(`/feed/users/${userId}/follow`); }
    catch {
      setFollowing(!optimistic);
      toast.show("Could not update follow.", "error");
    } finally { setBusy(false); }
  };

  if (error) {
    return (
      <div className="screen" data-testid="creator-error">
        <button
          className="icon-btn"
          onClick={() => navigate(-1)}
          style={{ marginBottom: 12 }}
          data-testid="creator-back"
        >
          <ChevronLeft size={18}/>
        </button>
        <div className="eyebrow" style={{ color: "var(--danger)" }}>NOT FOUND</div>
        <p className="body-sm">{error}</p>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="screen" style={{ alignItems: "center", justifyContent: "center" }}>
        <div className="spinner spinner-lg"/>
      </div>
    );
  }

  return (
    <div className="screen creator-screen" data-testid="creator-screen">
      <div className="creator-top">
        <button
          className="icon-btn"
          onClick={() => navigate(-1)}
          aria-label="Back"
          data-testid="creator-back"
        >
          <ChevronLeft size={20}/>
        </button>
        <div className="creator-handle">@{data.user_name}</div>
        <div style={{ width: 36 }}/>
      </div>

      <div className="creator-card">
        <div className="creator-avatar">{(data.user_name || "S")[0].toUpperCase()}</div>
        <div className="creator-name">@{data.user_name}</div>

        <div className="creator-stats">
          <div className="creator-stat">
            <div className="creator-stat-num" data-testid="creator-following-count">
              {compactNum(data.following_count)}
            </div>
            <div className="creator-stat-label">Following</div>
          </div>
          <div className="creator-stat">
            <div className="creator-stat-num" data-testid="creator-followers-count">
              {compactNum(data.followers_count)}
            </div>
            <div className="creator-stat-label">Followers</div>
          </div>
          <div className="creator-stat">
            <div className="creator-stat-num" data-testid="creator-posts-count">
              {compactNum(data.posts_count)}
            </div>
            <div className="creator-stat-label">Fits</div>
          </div>
        </div>

        {!data.is_self ? (
          <button
            type="button"
            className={`btn ${following ? "btn-outline" : "btn-primary"} creator-follow-btn`}
            onClick={toggleFollow}
            disabled={busy}
            data-testid="creator-follow-btn"
          >
            {following ? (<><UserCheck size={14}/> Following</>) : (<><UserPlus size={14}/> Follow</>)}
          </button>
        ) : (
          <button
            type="button"
            className="btn btn-outline creator-follow-btn"
            onClick={() => navigate("/feed/new")}
            data-testid="creator-post-btn"
          >
            + Post a fit
          </button>
        )}
      </div>

      <div className="eyebrow" style={{ margin: "8px 4px 12px" }}>POSTS</div>
      {data.posts.length === 0 ? (
        <div className="creator-empty" data-testid="creator-empty">
          <div className="tiny" style={{ color: "rgba(255,255,255,0.5)" }}>
            No fits yet.
          </div>
        </div>
      ) : (
        <div className="creator-grid" data-testid="creator-grid">
          {data.posts.map((p) => {
            const thumb = p.poster_url || (p.image_base64 ? `data:${p.image_mime || "image/jpeg"};base64,${p.image_base64}` : null);
            return (
              <button
                key={p.id}
                type="button"
                className="creator-tile"
                onClick={() => navigate(`/feed?post=${p.id}`)}
                data-testid={`creator-tile-${p.id}`}
              >
                {thumb && <img src={thumb} alt={p.caption || "post"} loading="lazy"/>}
                <div className="creator-tile-overlay">
                  {p.media_type === "video" ? <Play size={12} fill="#fff"/> : <ImageIcon size={12}/>}
                  <span>{compactNum(p.likes_count)}</span>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
